// src/pages/ClassesPage.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as api from "../services/api";
import Card from "../components/Card";
import Button from "../components/Button";
import Modal from "../components/Modal";
import Input from "../components/Input";
import {
  UserPlusIcon,
  ArrowPathIcon,
  AcademicCapIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import {
  STUDENT_FIELD_LABELS,
  STUDENT_FIELD_KEYS,
  generateAndSaveStudentsExcel,
} from "../utils/exportClassGroupsToExcel";

/* ---------------- Helpers (simplified & fixed start) ---------------- */

// Build a display name from possible student fields
const buildStudentDisplayName = (student = {}) => {
  const parts = [
    student.fullName,
    student.name,
    student.FirstName,
    student.firstName,
    student.FatherName,
    student.fatherName,
    student.LastName,
    student.lastName,
  ]
    .filter(Boolean)
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean);
  const joined = parts.join(" ").replace(/\s+/g, " ").trim();
  if (joined) return joined;
  return student.serialNumber ? `الرقم ${student.serialNumber}` : "اسم غير معروف";
};

// New fixed generator: start year 2025 and count 10 (2025-2026 ... 2034-2035)
const generateAcademicYearsFixed = (start = 2025, count = 10) =>
  Array.from({ length: count }).map((_, i) => `${start + i}-${start + i + 1}`);

// NEW helper: derive current academic year string like "2025-2026"
// Uses month >= July (7) to decide start year
const getCurrentAcademicYearString = (now = new Date()) => {
  const month = now.getMonth() + 1; // 1-12
  const year = now.getFullYear();
  const start = month >= 7 ? year : year - 1;
  return `${start}-${start + 1}`;
};

/* ---------------- CreateClassForm ---------------- */
const CreateClassForm = ({ onSave, onCancel, classNames = [], academicYears = [] }) => {
  const [className, setClassName] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [academicYear, setAcademicYear] = useState(academicYears[0] || "");
  const [verifiedStudents, setVerifiedStudents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const searchDebounce = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(searchDebounce.current);
    };
  }, []);

  useEffect(() => {
    // debounce search, ignore very short queries
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSearchResults([]);
      setLoadingSearch(false);
      return;
    }
    setLoadingSearch(true);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      try {
        const res = await api.searchStudents(searchQuery.trim());
        if (!mountedRef.current) return;
        let students = [];
        if (Array.isArray(res.data)) students = res.data;
        else if (Array.isArray(res.data?.students)) students = res.data.students;
        else if (Array.isArray(res.data?.data)) students = res.data.data;
        else students = res.data || [];

        // remove already added
        const filtered = students.filter((student) => {
          const id = student._id || student.id || student.serialNumber;
          return id && !verifiedStudents.some((s) => s.id === id);
        });
        setSearchResults(filtered);
      } catch (err) {
        console.error("searchStudents", err);
        setSearchResults([]);
        if (mountedRef.current) toast.error(err?.response?.data?.message || "فشل البحث عن طلاب");
      } finally {
        if (mountedRef.current) setLoadingSearch(false);
      }
    }, 420);

    return () => clearTimeout(searchDebounce.current);
  }, [searchQuery, verifiedStudents]);

  const addStudentToClass = (student) => {
    const id = student._id || student.id || student.serialNumber;
    if (!id) {
      toast.error("لا يمكن إضافة هذا الطالب (معرّف مفقود).");
      return;
    }
    if (verifiedStudents.some((s) => s.id === id)) {
      toast.warn("هذا الطالب مضاف بالفعل");
      return;
    }
    const standardized = { id, name: buildStudentDisplayName(student), serialNumber: student.serialNumber || "", raw: student };
    setVerifiedStudents((p) => [...p, standardized]);
    setSearchQuery("");
    setSearchResults([]);
    toast.success(`تمت إضافة ${standardized.name}`);
  };

  const removeStudent = (id) => setVerifiedStudents((p) => p.filter((s) => s.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!className || !roomNumber || !academicYear) {
      const msg = "الرجاء تعبئة جميع الحقول المطلوبة";
      setError(msg);
      toast.error(msg);
      return;
    }
    if (verifiedStudents.length === 0) {
      const msg = "أضف طالبًا واحدًا على الأقل";
      setError(msg);
      toast.error(msg);
      return;
    }

    const payload = {
      className,
      roomNumber,
      academicYear,
      students: verifiedStudents.map((s) => ({ student: s.id, serialNumber: s.serialNumber })),
    };

    setSubmitting(true);
    try {
      await onSave(payload);
    } catch (err) {
      const msg = err?.response?.data?.message || "فشل في إنشاء الشعبة";
      setError(msg);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">اسم الصف</label>
          <select value={className} onChange={(e) => setClassName(e.target.value)} className="w-full border rounded p-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="">اختر الصف</option>
            {classNames.map((name, idx) => (
              <option key={idx} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <Input label="رقم الشعبة" value={roomNumber} onChange={(e) => setRoomNumber(e.target.value)} required />

        <div>
          <label className="block text-sm font-medium mb-1">العام الدراسي</label>
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} className="w-full border rounded p-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="">اختر العام الدراسي</option>
            {academicYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <hr />

      <div className="relative" style={{ zIndex: 50 }}>
        <label className="text-sm font-medium text-gray-700 block mb-2">
          <MagnifyingGlassIcon className="h-5 w-5 inline-block ml-1 text-gray-500" />
          بحث وإضافة طالب (ثلاثة أحرف على الأقل)
        </label>
        <Input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
          placeholder="ابحث بالاسم أو الرقم التسلسلي..."
          aria-label="بحث عن طالب"
        />

        {loadingSearch && (
          <div className="text-blue-500 text-sm mt-2 flex items-center justify-center py-2 bg-blue-50 rounded-md">جارٍ البحث...</div>
        )}

        {searchResults.length > 0 && !loadingSearch && (
          <div className="absolute z-50 left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl max-h-[360px] overflow-y-auto ring-1 ring-black ring-opacity-5" role="listbox" aria-label="نتائج البحث">
            {searchResults.map((student) => {
              const key = student._id || student.id || student.serialNumber || JSON.stringify(student).slice(0, 12);
              return (
                <div key={key} onClick={() => addStudentToClass(student)} className="flex justify-between items-center p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0">
                  <div>
                    <p className="font-semibold text-gray-800">{buildStudentDisplayName(student)}</p>
                    <p className="text-sm text-gray-500">الرقم التسلسلي: {student.serialNumber || '—'}</p>
                  </div>
                  <Button type="button" size="sm" className="bg-green-600 hover:bg-green-700 text-white shadow-md">+ إضافة</Button>
                </div>
              );
            })}
          </div>
        )}

        {searchResults.length === 0 && searchQuery.trim().length >= 3 && !loadingSearch && (
          <div className="text-gray-500 text-sm mt-2 text-center py-2 bg-gray-100 rounded-md">لا توجد نتائج مطابقة</div>
        )}
      </div>

      {verifiedStudents.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="font-semibold text-gray-700">✅ الطلاب المضافون ({verifiedStudents.length}):</h4>
          <div className="max-h-48 overflow-y-auto border p-2 rounded-lg bg-white shadow-inner">
            {verifiedStudents.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border-b last:border-b-0">
                <span className="text-gray-800">{s.name} {s.serialNumber ? `(${s.serialNumber})` : ''}</span>
                <button type="button" onClick={() => removeStudent(s.id)} className="text-red-600 hover:text-red-800 transition duration-150 p-1 rounded-full">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-red-500 bg-red-50 p-2 rounded-lg mt-4">{error}</p>}

      <div className="flex justify-end gap-2 pt-4 border-t mt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          <XMarkIcon className="h-5 w-5 ml-1" /> إلغاء
        </Button>
        <Button type="submit" disabled={submitting || verifiedStudents.length === 0} className="bg-blue-600 hover:bg-blue-700">
          {submitting ? 'جارٍ الحفظ...' : (<><CheckIcon className="h-5 w-5 ml-1" /> حفظ وإنشاء الشعبة</>)}
        </Button>
      </div>
    </form>
  );
};

/* ---------------- ExportSelectGroupsModal ----------------
   Step 1 of the Excel export flow: pick which class groups (شعب) to include.
   The list comes pre-filtered by whatever academic year the parent ClassesPage
   has selected; an empty academic-year filter means "all years" and the modal
   shows every group with the year shown beside it for disambiguation.
------------------------------------------------------------ */
const ExportSelectGroupsModal = ({
  isOpen,
  onClose,
  groups,
  loading,
  academicYear,
  onConfirm,
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Controls the "تجميع الطلاب حسب رقم الشعبة" toggle. Default ON so the
  // existing per-room banded layout is preserved unless the user opts out.
  const [groupByRoom, setGroupByRoom] = useState(true);

  // Reset selection whenever the modal re-opens or the underlying list changes
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setGroupByRoom(true);
    }
  }, [isOpen, groups]);

  const allChecked =
    groups.length > 0 && selectedIds.size === groups.length;
  const someChecked =
    selectedIds.size > 0 && selectedIds.size < groups.length;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(groups.map((g) => g._id || g.id)));
  };

  const handleConfirm = () => {
    if (selectedIds.size === 0) {
      toast.error("يجب اختيار شعبة واحدة على الأقل");
      return;
    }
    const chosen = groups.filter((g) =>
      selectedIds.has(g._id || g.id)
    );
    onConfirm(chosen, { groupByRoom });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="📋 اختيار الشعب لتخريج معلومات الطلاب"
    >
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
          {academicYear
            ? <>العام الدراسي المختار: <strong>{academicYear}</strong></>
            : "العام الدراسي: الكل (سيتم عرض جميع الشعب من جميع الأعوام)"}
        </div>

        {/* Grouping mode toggle ----------------------------------------
            Default: students inside each sheet are banded by الشعبة رقم.
            Off: every student of the same الصف is listed continuously,
                 ignoring which room they came from.
        ----------------------------------------------------------------- */}
        <label className="flex items-start gap-3 p-3 border border-amber-200 bg-amber-50 rounded cursor-pointer select-none">
          <input
            type="checkbox"
            checked={groupByRoom}
            onChange={(e) => setGroupByRoom(e.target.checked)}
            className="h-5 w-5 mt-0.5 shrink-0"
          />
          <div>
            <div className="font-semibold text-amber-900">
              تجميع الطلاب حسب رقم الشعبة داخل كل صف
            </div>
            <div className="text-xs text-amber-800 mt-1 leading-relaxed">
              {groupByRoom
                ? "مُفعّل: داخل كل ورقة (صف) ستظهر الشعب مفصولة بعناوين «الشعبة رقم: N»."
                : "مُعطّل: سيتم دمج طلاب جميع شعب الصف الواحد في قائمة واحدة بدون فواصل."}
            </div>
          </div>
        </label>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-600">
            جارٍ تحميل قائمة الشعب...
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-10 text-gray-500 bg-gray-50 rounded border border-dashed">
            لا توجد شعب لهذا الفلتر
          </div>
        ) : (
          <>
            <label className="flex items-center gap-2 px-2 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 select-none">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked;
                }}
                onChange={toggleAll}
                className="h-5 w-5"
              />
              <span className="font-semibold">
                {allChecked ? "إلغاء اختيار الكل" : "اختيار الكل"}
              </span>
              <span className="text-sm text-gray-500 mr-auto">
                ({selectedIds.size} / {groups.length})
              </span>
            </label>

            <div className="max-h-72 overflow-y-auto border rounded">
              {groups.map((g) => {
                const id = g._id || g.id;
                const checked = selectedIds.has(id);
                return (
                  <label
                    key={id}
                    className={`flex items-center gap-2 px-3 py-2 border-b last:border-b-0 cursor-pointer ${
                      checked ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(id)}
                      className="h-4 w-4"
                    />
                    <span className="font-semibold text-gray-800">
                      {g.className} - {g.roomNumber}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({g.academicYear})
                    </span>
                    <span className="mr-auto text-xs text-gray-500">
                      {(g.students || []).length} طلاب
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            <XMarkIcon className="h-5 w-5 ml-1" /> إلغاء
          </Button>
          <Button
            type="button"
            disabled={loading || groups.length === 0}
            onClick={handleConfirm}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <CheckIcon className="h-5 w-5 ml-1" /> التالي
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/* ---------------- ExportSelectColumnsModal ----------------
   Step 2 of the Excel export flow: pick which Student columns end up in
   the file. Defaults to all columns checked. Confirming triggers the
   actual workbook generation + Save-As dialog.
------------------------------------------------------------ */
const ExportSelectColumnsModal = ({
  isOpen,
  onClose,
  onConfirm,
  exporting,
  selectedGroupsCount,
  groupByRoom,
}) => {
  const [selectedFields, setSelectedFields] = useState(
    () => new Set(STUDENT_FIELD_KEYS)
  );

  // Default to "all checked" each time the modal re-opens
  useEffect(() => {
    if (isOpen) setSelectedFields(new Set(STUDENT_FIELD_KEYS));
  }, [isOpen]);

  const allChecked = selectedFields.size === STUDENT_FIELD_KEYS.length;
  const someChecked =
    selectedFields.size > 0 &&
    selectedFields.size < STUDENT_FIELD_KEYS.length;

  const toggleOne = (key) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (allChecked) setSelectedFields(new Set());
    else setSelectedFields(new Set(STUDENT_FIELD_KEYS));
  };

  const handleConfirm = () => {
    if (selectedFields.size === 0) {
      toast.error("يجب اختيار عمود واحد على الأقل");
      return;
    }
    // Preserve the canonical column order (not selection order)
    const ordered = STUDENT_FIELD_KEYS.filter((k) => selectedFields.has(k));
    onConfirm(ordered);
  };

  return (
    <Modal
      isOpen={isOpen}
      // While exporting, swallow outside-click / Esc close attempts so the
      // user can't accidentally dismiss the modal mid-write.
      onClose={exporting ? () => {} : onClose}
      title="📑 اختيار الأعمدة المراد تخريجها"
    >
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 space-y-1">
          <div>
            عدد الشعب المختارة: <strong>{selectedGroupsCount}</strong>. سيتم
            إنشاء ورقة (Sheet) لكل صف تحتوي على جميع شعبه مجمعة.
          </div>
          <div className="text-xs">
            طريقة العرض داخل كل ورقة:{" "}
            <strong>
              {groupByRoom
                ? "تجميع الطلاب حسب رقم الشعبة (مع فواصل)"
                : "قائمة موحّدة بدون فواصل الشعب"}
            </strong>
          </div>
        </div>

        <label className="flex items-center gap-2 px-2 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 select-none">
          <input
            type="checkbox"
            checked={allChecked}
            ref={(el) => {
              if (el) el.indeterminate = someChecked;
            }}
            onChange={toggleAll}
            className="h-5 w-5"
          />
          <span className="font-semibold">
            {allChecked ? "إلغاء اختيار الكل" : "اختيار جميع الأعمدة"}
          </span>
          <span className="text-sm text-gray-500 mr-auto">
            ({selectedFields.size} / {STUDENT_FIELD_KEYS.length})
          </span>
        </label>

        <div className="max-h-80 overflow-y-auto border rounded grid grid-cols-1 sm:grid-cols-2">
          {STUDENT_FIELD_KEYS.map((key) => {
            const checked = selectedFields.has(key);
            return (
              <label
                key={key}
                className={`flex items-center gap-2 px-3 py-2 border-b cursor-pointer ${
                  checked ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(key)}
                  className="h-4 w-4"
                />
                <span className="text-gray-800">
                  {STUDENT_FIELD_LABELS[key]}
                </span>
              </label>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={exporting}
          >
            <XMarkIcon className="h-5 w-5 ml-1" /> إلغاء
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={exporting}
            className="bg-green-600 hover:bg-green-700"
          >
            {exporting ? (
              <>جارٍ تحضير الملف...</>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-5 w-5 ml-1" />
                تخريج الملف
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/* ---------------- ClassesPage with Backend Pagination ---------------- */
const ClassesPage = () => {
  const academicYears = useMemo(() => generateAcademicYearsFixed(2025, 10), []); // fixed range
  const classNames = useMemo(
    () => [
      "الأول",
      "الثاني",
      "الثالث",
      "الرابع",
      "الخامس",
      "السادس",
      "السابع",
      "الثامن",
      "التاسع",
      "العاشر",
      "الحادي عشر",
    ],
    []
  );

  const [classGroups, setClassGroups] = useState([]);

  // ---- compute the "current" academic year and default to it when present in the list.
  const computedCurrentAcademicYear = useMemo(() => getCurrentAcademicYearString(), []);
  const currentAcademicYear = useMemo(
    () => (academicYears.includes(computedCurrentAcademicYear) ? computedCurrentAcademicYear : academicYears[0]),
    [academicYears, computedCurrentAcademicYear]
  );

  const [academicYear, setAcademicYear] = useState(currentAcademicYear);
  // ---- end change

  const [className, setClassName] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => (mountedRef.current = false);
  }, []);

  useEffect(() => {
    const navState = location.state;
    // Check if we were told to auto-open the modal
    if (navState?.autoOpenModal) {
      setIsModalOpen(true);
      // IMPORTANT: Clear the state so opening only happens once
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // ---------------- Pagination state ----------------
  const PAGE_SIZE = 12; // server-side limit per page
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // --------------------------------------------------

  // ---------------- Excel export state ----------------
  const [isExportGroupsOpen, setIsExportGroupsOpen] = useState(false);
  const [isExportColumnsOpen, setIsExportColumnsOpen] = useState(false);
  const [exportGroupOptions, setExportGroupOptions] = useState([]);
  const [exportLoadingList, setExportLoadingList] = useState(false);
  const [exportSelectedGroups, setExportSelectedGroups] = useState([]); // holds the basic ClassGroup rows (with id) the user picked
  // Layout option picked in modal 1: when true, each sheet bands rows by
  // roomNumber; when false, students of the same الصف are listed flat.
  const [exportGroupByRoom, setExportGroupByRoom] = useState(true);
  const [exporting, setExporting] = useState(false);
  // ---------------------------------------------------

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      // send filters + pagination to server
      const params = {
        academicYear: academicYear || undefined,
        className: className || undefined,
        roomNumber: roomNumber || undefined,
        page: currentPage,
        limit: PAGE_SIZE,
      };

      const res = await api.getClassGroups(params);
      // The API wrapper may return data nested in different shapes; handle commonly used patterns
      // Common expected payload: { data: groups, currentPage, totalPages, totalCount }
 const payload = res.data ?? res;

// Extract groups safely
const groups =
  (Array.isArray(payload?.data) && payload.data) ||
  (Array.isArray(payload?.data?.data) && payload.data.data) ||
  (Array.isArray(payload) && payload) ||
  [];

// Extract page info safely
const serverCurrentPage =
  payload.currentPage ??
  payload?.data?.currentPage ??
  currentPage;

const serverTotalPages =
  payload.totalPages ??
  payload?.data?.totalPages ??
  1;

const serverTotalCount =
  payload.totalCount ??
  payload?.data?.totalCount ??
  (Array.isArray(groups) ? groups.length : 0);

if (!mountedRef.current) return;

setClassGroups(groups);
setTotalPages(Number(serverTotalPages) || 1);
setTotalCount(Number(serverTotalCount) || groups.length);
setCurrentPage(Number(serverCurrentPage) || currentPage);

    } catch (err) {
      console.error("Failed to fetch classes", err);
      if (mountedRef.current) toast.error("فشل تحميل الشعب");
      if (mountedRef.current) {
        setClassGroups([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [academicYear, className, roomNumber, currentPage]);

  // Debounce fetch when filters or page change
  const fetchTimer = useRef(null);
  useEffect(() => {
    clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(() => fetchClasses(), 280);
    return () => clearTimeout(fetchTimer.current);
  }, [academicYear, className, roomNumber, currentPage, fetchClasses]);

  const handleSave = async (classData) => {
    try {
      await api.addClassGroup(classData);
      setIsModalOpen(false);
      // re-fetch page 1 to include new class (server might place the new class in page 1)
      setCurrentPage(1);
      await fetchClasses();
      toast.success("✅ تم إنشاء الشعبة بنجاح");
    } catch (err) {
      const errorMessage = err.response?.data?.message || "❌ فشل إنشاء الشعبة";
      toast.error(errorMessage, { autoClose: 10000 });
      throw err;
    }
  };

  const resetFilters = () => {
    setAcademicYear(currentAcademicYear);
    setClassName("");
    setRoomNumber("");
    setCurrentPage(1);
  };

  const isFilterActive = academicYear !== currentAcademicYear || className || roomNumber;

  // ---------------- Excel export handlers ----------------
  // Step 1: open the "select groups" modal. Loads ALL class groups for the
  // currently selected academic-year filter (and any className/roomNumber
  // filters too) by hitting the same endpoint with a high `limit` so we
  // bypass pagination for the modal listing.
  const handleOpenExportModal = async () => {
    setIsExportGroupsOpen(true);
    setExportLoadingList(true);
    setExportGroupOptions([]);
    try {
      const res = await api.getClassGroups({
        academicYear: academicYear || undefined,
        className: className || undefined,
        roomNumber: roomNumber || undefined,
        page: 1,
        limit: 1000,
      });
      const payload = res.data ?? res;
      const groups =
        (Array.isArray(payload?.data) && payload.data) ||
        (Array.isArray(payload) && payload) ||
        [];
      if (mountedRef.current) setExportGroupOptions(groups);
    } catch (err) {
      console.error("Failed to load class groups for export", err);
      toast.error("فشل تحميل قائمة الشعب");
      if (mountedRef.current) setExportGroupOptions([]);
    } finally {
      if (mountedRef.current) setExportLoadingList(false);
    }
  };

  // Step 1 → Step 2 transition: remember the picked groups + layout option,
  // then swap modals.
  const handleConfirmExportGroups = (chosenGroups, options = {}) => {
    setExportSelectedGroups(chosenGroups);
    setExportGroupByRoom(options.groupByRoom !== false); // default to true
    setIsExportGroupsOpen(false);
    setIsExportColumnsOpen(true);
  };

  // Step 2: pull the FULL Student rows for the picked groups (the listing
  // endpoint only returns 4 student attributes), generate the workbook,
  // then prompt the user to choose where to save it.
  const handleConfirmExportColumns = async (selectedFields) => {
    if (exportSelectedGroups.length === 0) {
      toast.error("لم يتم تحديد أي شعبة");
      return;
    }
    setExporting(true);
    try {
      const ids = exportSelectedGroups
        .map((g) => g._id || g.id)
        .filter(Boolean);

      const { data } = await api.getClassGroupsForExport(ids);
      const fullGroups = data?.data || [];

      if (fullGroups.length === 0) {
        toast.error("لم يتم العثور على بيانات الشعب المختارة");
        return;
      }

      // Pick a sensible default file name. If every selected group is from
      // the same academic year, include it; otherwise use a generic name.
      const years = Array.from(
        new Set(fullGroups.map((g) => g.academicYear).filter(Boolean))
      );
      const suggestedFileName =
        years.length === 1
          ? `معلومات_الطلاب_${years[0]}`
          : "معلومات_الطلاب";

      const result = await generateAndSaveStudentsExcel({
        classGroups: fullGroups,
        selectedFields,
        groupByRoom: exportGroupByRoom,
        suggestedFileName,
      });

      if (result.cancelled) {
        toast.info("تم إلغاء حفظ الملف");
      } else if (result.saved) {
        toast.success("✅ تم تخريج الملف بنجاح");
        setIsExportColumnsOpen(false);
        setExportSelectedGroups([]);
      }
    } catch (err) {
      console.error("Excel export failed:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "فشل تخريج ملف Excel"
      );
    } finally {
      if (mountedRef.current) setExporting(false);
    }
  };

  const handleCloseExportColumns = () => {
    if (exporting) return; // don't allow closing mid-export
    setIsExportColumnsOpen(false);
    setExportSelectedGroups([]);
  };
  // -------------------------------------------------------

  // Pagination helper: render compact page buttons (show window around current page)
  const renderPageButtons = () => {
    const pages = [];
    const visibleWindow = 5; // show current ±2
    const half = Math.floor(visibleWindow / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, currentPage + half);

    // adjust window when near edges
    if (currentPage - half < 1) {
      end = Math.min(totalPages, end + (1 - (currentPage - half)));
    }
    if (currentPage + half > totalPages) {
      start = Math.max(1, start - ((currentPage + half) - totalPages));
    }

    // first page + ellipsis
    if (start > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => setCurrentPage(1)}
          className={`px-3 py-1 border rounded ${currentPage === 1 ? "bg-blue-600 text-white" : "bg-white"}`}
        >
          1
        </button>
      );
      if (start > 2) {
        pages.push(
          <span key="start-ellipsis" className="px-2 text-gray-500 select-none">...</span>
        );
      }
    }

    for (let p = start; p <= end; p++) {
      pages.push(
        <button
          key={p}
          onClick={() => setCurrentPage(p)}
          className={`px-3 py-1 border rounded ${currentPage === p ? "bg-blue-600 text-white" : "bg-white"}`}
        >
          {p}
        </button>
      );
    }

    // last page + ellipsis
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push(<span key="end-ellipsis" className="px-2 text-gray-500 select-none">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
          className={`px-3 py-1 border rounded ${currentPage === totalPages ? "bg-blue-600 text-white" : "bg-white"}`}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <Card title="📚 إدارة الشعب الدراسية">
      <div className="mb-6">
        <h4 className="text-gray-700 font-semibold mb-3">🔎 خيارات البحث / الفلترة</h4>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            <div className="w-full md:w-64">
              <label className="sr-only">العام الدراسي</label>
              <select
                value={academicYear}
                onChange={(e) => {
                  setAcademicYear(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border rounded p-2"
              >
                <option value="">الكل (العام الدراسي)</option>
                {academicYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full md:w-64">
              <label className="sr-only">اسم الصف</label>
              <select
                value={className}
                onChange={(e) => {
                  setClassName(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border rounded p-2"
              >
                <option value="">الكل (اسم الصف)</option>
                {classNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <Input
              placeholder="🏫 رقم الشعبة"
              value={roomNumber}
              onChange={(e) => {
                setRoomNumber(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:w-64"
            />

            {isFilterActive && (
              <Button variant="secondary" onClick={resetFilters} className="flex items-center w-full md:w-auto mt-2 md:mt-0">
                <ArrowPathIcon className="h-5 w-5 ml-1" /> إعادة ضبط الفلاتر
              </Button>
            )}
          </div>

          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <Button
              onClick={handleOpenExportModal}
              className="flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 w-full md:w-auto"
              title="تخريج معلومات الطلاب لشعبة أو أكثر إلى ملف Excel"
            >
              <TableCellsIcon className="h-5 w-5 ml-2" />
              تخريج معلومات الطلاب في ملف excel
            </Button>
            <Button onClick={() => setIsModalOpen(true)} className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 w-full md:w-auto">
              <UserPlusIcon className="h-5 w-5 ml-2" /> إنشاء شعبة جديدة
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-200 rounded-xl" />
          ))}
        </div>
      ) : classGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500 bg-gray-50 rounded-xl border border-dashed">
          <AcademicCapIcon className="h-12 w-12 mb-3 text-gray-400" />
          <p className="text-xl font-semibold">لا توجد شعب مطابقة للبحث</p>
          <p className="text-md mt-1">جرّب تعديل العام الدراسي أو إنشاء شعبة جديدة.</p>
        </div>
      ) : (
        <div>
          <div className="mb-4 text-lg text-slate-1000">عدد الشعب لهذا العام الدراسي :  {totalCount}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {classGroups.map((group) => (
              <div
                key={group._id || group.id || `${group.className}-${group.roomNumber}`}
                onClick={() => navigate(`/classes/${group._id || group.id}`)}
                className="p-5 bg-white border rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-[1.01] flex flex-col justify-between"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/classes/${group._id || group.id}`); }}
              >
                <h3 className="font-bold text-xl text-blue-700">{group.className} - {group.roomNumber}</h3>
                <div>
                  <p className="text-md text-slate-1000 mt-1">{group.academicYear}</p>
                  <p className="text-md text-slate-1000 mt-2">{(group.students || []).length} طلاب مسجلون</p>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination controls */}
          <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              الأول
            </button>

            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              السابق
            </button>

            <div className="flex items-center gap-2">
              {renderPageButtons()}
            </div>

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              التالي
            </button>

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              الأخير
            </button>
          </div>

          <div className="text-center text-sm text-gray-500 mt-2">
            صفحة {currentPage} من {totalPages} — إجمالي الشعب: {totalCount}
          </div>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="إنشاء شعبة جديدة">
        <CreateClassForm onSave={handleSave} onCancel={() => setIsModalOpen(false)} classNames={classNames} academicYears={academicYears} />
      </Modal>

      {/* Excel export — step 1: pick the class groups (شعب) */}
      <ExportSelectGroupsModal
        isOpen={isExportGroupsOpen}
        onClose={() => setIsExportGroupsOpen(false)}
        groups={exportGroupOptions}
        loading={exportLoadingList}
        academicYear={academicYear}
        onConfirm={handleConfirmExportGroups}
      />

      {/* Excel export — step 2: pick the Student columns + generate */}
      <ExportSelectColumnsModal
        isOpen={isExportColumnsOpen}
        onClose={handleCloseExportColumns}
        onConfirm={handleConfirmExportColumns}
        exporting={exporting}
        selectedGroupsCount={exportSelectedGroups.length}
        groupByRoom={exportGroupByRoom}
      />
    </Card>
  );
};

export default ClassesPage;
